import React, { useState } from 'react';

const STATUS_BADGE = {
  assigned: { label: 'Action Required', bg: 'rgba(245, 166, 35, 0.15)', border: 'rgba(245, 166, 35, 0.4)', text: '#f5a623' },
  accepted: { label: 'Accepted / Scheduled', bg: 'rgba(0, 210, 255, 0.15)', border: 'rgba(0, 210, 255, 0.4)', text: '#00D2FF' },
  in_progress: { label: 'In Progress (Live)', bg: 'rgba(159, 92, 255, 0.2)', border: 'rgba(159, 92, 255, 0.5)', text: '#9F5CFF' },
  completed: { label: 'Completed', bg: 'rgba(39, 200, 90, 0.15)', border: 'rgba(39, 200, 90, 0.4)', text: '#27C85A' },
  declined: { label: 'Declined', bg: 'rgba(227, 67, 100, 0.15)', border: 'rgba(227, 67, 100, 0.4)', text: '#E34364' },
};

export default function AssignmentsView({
  events,
  loading,
  currentTab,
  setCurrentTab,
  onRespond,
  onSelectLiveEvent,
  onOpenCompleteModal,
}) {
  const [declineModal, setDeclineModal] = useState(null); // event object
  const [declineReason, setDeclineReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const tabs = [
    { id: 'all', label: 'All Events' },
    { id: 'requests', label: '⚡ Requests', badgeKey: 'assigned' },
    { id: 'upcoming', label: '📅 Upcoming', badgeKey: 'accepted' },
    { id: 'in_progress', label: '🔴 Live Now', badgeKey: 'in_progress' },
    { id: 'completed', label: '✅ Completed', badgeKey: 'completed' },
  ];

  const handleDeclineSubmit = async (e) => {
    e.preventDefault();
    if (!declineModal) return;
    setActionLoading(true);
    await onRespond(declineModal.id, 'decline', declineReason);
    setActionLoading(false);
    setDeclineModal(null);
    setDeclineReason('');
  };

  const getBadgeCount = (badgeKey) => {
    return events.filter((e) => e.assignment_status === badgeKey).length;
  };

  return (
    <div>
      {/* Tab Filter Bar */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          overflowX: 'auto',
          paddingBottom: 8,
          marginBottom: 20,
          scrollbarWidth: 'none',
        }}
      >
        {tabs.map((tab) => {
          const isActive = currentTab === tab.id;
          const count = tab.badgeKey ? getBadgeCount(tab.badgeKey) : 0;
          return (
            <button
              key={tab.id}
              onClick={() => setCurrentTab(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                borderRadius: '50px',
                border: isActive
                  ? '1px solid #9F5CFF'
                  : '1px solid rgba(255, 255, 255, 0.08)',
                background: isActive
                  ? 'linear-gradient(135deg, rgba(159,92,255,0.25) 0%, rgba(159,92,255,0.1) 100%)'
                  : 'rgba(255, 255, 255, 0.03)',
                color: isActive ? '#fff' : '#a0a0b8',
                fontSize: 13,
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
            >
              <span>{tab.label}</span>
              {count > 0 && (
                <span
                  style={{
                    background: tab.id === 'requests' ? '#f5a623' : '#9F5CFF',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 800,
                    padding: '1px 6px',
                    borderRadius: 10,
                  }}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Events List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#a0a0b8' }}>
          <div
            style={{
              width: 36,
              height: 36,
              border: '3px solid rgba(159, 92, 255, 0.2)',
              borderTopColor: '#9F5CFF',
              borderRadius: '50%',
              margin: '0 auto 12px',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <p style={{ fontSize: 13 }}>Loading assigned events…</p>
        </div>
      ) : events.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 20px',
            background: 'rgba(255,255,255,0.02)',
            borderRadius: '16px',
            border: '1px dashed rgba(255,255,255,0.08)',
          }}
        >
          <span style={{ fontSize: 40, display: 'block', marginBottom: 10 }}>🎉</span>
          <h3 style={{ margin: '0 0 6px', fontSize: 16, color: '#fff' }}>No Events in This Tab</h3>
          <p style={{ margin: 0, fontSize: 13, color: '#7c7c9c' }}>
            Check other tabs or wait for new admin assignments to appear.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
          {events.map((event) => {
            const isAssigned = event.assignment_status === 'assigned';
            const isAccepted = event.assignment_status === 'accepted';
            const isInProgress = event.assignment_status === 'in_progress';
            const isCompleted = event.assignment_status === 'completed';

            const badge = STATUS_BADGE[event.assignment_status] || {
              label: event.assignment_status || 'Pending',
              bg: 'rgba(255,255,255,0.1)',
              border: 'rgba(255,255,255,0.2)',
              text: '#fff',
            };

            const mapQuery = encodeURIComponent(
              `${event.venue_address || ''} ${event.venue_name || ''}`.trim()
            );

            // Parse selected addons
            let addons = event.selected_addons;
            if (typeof addons === 'string') {
              try {
                addons = JSON.parse(addons);
              } catch (_) {
                addons = [];
              }
            }
            if (!Array.isArray(addons)) addons = [];

            // Parse extras
            let extras = event.booking_extras;
            if (typeof extras === 'string') {
              try {
                extras = JSON.parse(extras);
              } catch (_) {
                extras = [];
              }
            }
            if (!Array.isArray(extras)) extras = [];

            return (
              <div
                key={event.id}
                style={{
                  background: isAssigned
                    ? 'linear-gradient(145deg, #181938 0%, #101228 100%)'
                    : 'linear-gradient(145deg, #101428 0%, #0d0f22 100%)',
                  borderRadius: 16,
                  border: isAssigned
                    ? '1px solid rgba(245, 166, 35, 0.45)'
                    : isInProgress
                    ? '1px solid rgba(159, 92, 255, 0.45)'
                    : '1px solid rgba(255, 255, 255, 0.08)',
                  boxShadow: isAssigned
                    ? '0 10px 30px rgba(245, 166, 35, 0.1)'
                    : '0 8px 24px rgba(0,0,0,0.3)',
                  overflow: 'hidden',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
              >
                {/* Assignment Alert Banner (Action Required) */}
                {isAssigned && (
                  <div
                    style={{
                      background: 'rgba(245, 166, 35, 0.15)',
                      borderBottom: '1px solid rgba(245, 166, 35, 0.3)',
                      padding: '10px 18px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 16 }}>⚡</span>
                      <span style={{ color: '#f5a623', fontSize: 13, fontWeight: 700 }}>
                        New Event Assignment • Accept to reserve in your schedule
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => onRespond(event.id, 'accept')}
                        style={{
                          background: '#27C85A',
                          border: 'none',
                          borderRadius: 8,
                          color: '#fff',
                          padding: '6px 14px',
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        ✓ Accept Event
                      </button>
                      <button
                        onClick={() => setDeclineModal(event)}
                        style={{
                          background: 'rgba(227, 67, 100, 0.15)',
                          border: '1px solid rgba(227, 67, 100, 0.4)',
                          borderRadius: 8,
                          color: '#E34364',
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        ✕ Decline
                      </button>
                    </div>
                  </div>
                )}

                {/* Card Main Body */}
                <div style={{ padding: '20px' }}>
                  {/* Top Bar: Booking ID + Status + Scheduled Hours */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 14,
                      flexWrap: 'wrap',
                      gap: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 12,
                          color: '#a0a0b8',
                          background: 'rgba(255,255,255,0.06)',
                          padding: '3px 8px',
                          borderRadius: 6,
                        }}
                      >
                        BKG-{String(event.id).padStart(5, '0')}
                      </span>
                      <span
                        style={{
                          background: badge.bg,
                          border: `1px solid ${badge.border}`,
                          color: badge.text,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderRadius: 20,
                          textTransform: 'uppercase',
                        }}
                      >
                        {badge.label}
                      </span>
                    </div>

                    <div style={{ fontSize: 13, color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ color: '#7c7c9c' }}>Duration:</span>
                      <strong style={{ color: '#9F5CFF' }}>
                        {event.scheduled_hours || event.duration_hours || 3} Hours
                      </strong>
                    </div>
                  </div>

                  {/* Date & Time Header */}
                  <div
                    style={{
                      background: 'rgba(255,255,255,0.02)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 12,
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: 16,
                      flexWrap: 'wrap',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, color: '#7c7c9c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Event Date
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginTop: 2 }}>
                        📅 {event.event_date}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 11, color: '#7c7c9c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Coverage Times
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#00D2FF', marginTop: 2 }}>
                        🕒 {event.start_time} - {event.end_time}
                      </div>
                    </div>
                  </div>

                  {/* Venue & Location Box */}
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#7c7c9c', fontWeight: 600, marginBottom: 4 }}>
                      VENUE & LOCATION
                    </div>
                    <div style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>
                      🏛️ {event.venue_name || 'Venue to be confirmed'}
                    </div>
                    {event.venue_address ? (
                      <div style={{ fontSize: 13, color: '#a0a0b8', marginTop: 2 }}>
                        📍 {event.venue_address}
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#7c7c9c', fontStyle: 'italic', marginTop: 2 }}>
                        No street address entered yet
                      </div>
                    )}

                    {mapQuery && (
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          color: '#00D2FF',
                          textDecoration: 'none',
                          marginTop: 6,
                          fontWeight: 600,
                        }}
                      >
                        🗺️ Open in Google Maps ↗
                      </a>
                    )}
                  </div>

                  {/* Client Contact & Details */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'rgba(255,255,255,0.03)',
                      borderRadius: 12,
                      padding: '12px 16px',
                      marginBottom: 16,
                      flexWrap: 'wrap',
                      gap: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 11, color: '#7c7c9c' }}>CLIENT CONTACT</div>
                      <div style={{ fontSize: 13, color: '#fff', fontWeight: 600, marginTop: 2 }}>
                        👤 {event.customer?.name || 'Client'}
                      </div>
                      {event.customer?.email && (
                        <div style={{ fontSize: 12, color: '#a0a0b8' }}>{event.customer.email}</div>
                      )}
                    </div>

                    {(event.phone_number || event.customer?.phone_number) && (
                      <a
                        href={`tel:${event.phone_number || event.customer?.phone_number}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          background: 'rgba(39, 200, 90, 0.15)',
                          border: '1px solid rgba(39, 200, 90, 0.35)',
                          color: '#27C85A',
                          padding: '8px 14px',
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 700,
                          textDecoration: 'none',
                        }}
                      >
                        📞 Call Client
                      </a>
                    )}
                  </div>

                  {/* Booth Type, Style, Wording & Theme */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: 12,
                      marginBottom: 16,
                      fontSize: 12,
                    }}
                  >
                    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: 10 }}>
                      <span style={{ color: '#7c7c9c', display: 'block' }}>Booth Hardware:</span>
                      <strong style={{ color: '#fff', fontSize: 13 }}>{event.booth_type}</strong>
                    </div>

                    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: 10 }}>
                      <span style={{ color: '#7c7c9c', display: 'block' }}>Photo Strip Style:</span>
                      <strong style={{ color: '#f5a623', fontSize: 13 }}>
                        🎞️ {event.photo_strip_style || '2×6 (Vertical)'}
                      </strong>
                    </div>

                    {event.backdrop && (
                      <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px 14px', borderRadius: 10 }}>
                        <span style={{ color: '#7c7c9c', display: 'block' }}>Backdrop:</span>
                        <strong style={{ color: '#fff', fontSize: 13 }}>🖼️ {event.backdrop.name}</strong>
                      </div>
                    )}
                  </div>

                  {/* Custom Strip Wordings & Theme */}
                  {event.strip_wordings && (
                    <div
                      style={{
                        background: 'rgba(245, 166, 35, 0.08)',
                        borderLeft: '3px solid #f5a623',
                        padding: '10px 14px',
                        borderRadius: '0 8px 8px 0',
                        marginBottom: 12,
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: '#f5a623', fontWeight: 700 }}>Custom Print Text: </span>
                      <span style={{ color: '#fff' }}>"{event.strip_wordings}"</span>
                    </div>
                  )}

                  {event.event_theme_description && (
                    <div
                      style={{
                        background: 'rgba(159, 92, 255, 0.08)',
                        borderLeft: '3px solid #9F5CFF',
                        padding: '10px 14px',
                        borderRadius: '0 8px 8px 0',
                        marginBottom: 12,
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: '#9F5CFF', fontWeight: 700 }}>Theme / Vision: </span>
                      <span style={{ color: '#fff' }}>{event.event_theme_description}</span>
                    </div>
                  )}

                  {/* Admin Instructions / Notes */}
                  {event.admin_notes && (
                    <div
                      style={{
                        background: 'rgba(0, 210, 255, 0.08)',
                        borderLeft: '3px solid #00D2FF',
                        padding: '10px 14px',
                        borderRadius: '0 8px 8px 0',
                        marginBottom: 12,
                        fontSize: 12,
                      }}
                    >
                      <span style={{ color: '#00D2FF', fontWeight: 700 }}>Admin Instructions: </span>
                      <span style={{ color: '#fff' }}>{event.admin_notes}</span>
                    </div>
                  )}

                  {/* Add-ons & Mid-Event Extras */}
                  {(addons.length > 0 || extras.length > 0) && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 11, color: '#7c7c9c', fontWeight: 700, marginBottom: 6 }}>
                        ADD-ONS & INCLUDED EQUIPMENT
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {addons.map((a, i) => (
                          <span
                            key={i}
                            style={{
                              background: 'rgba(255,255,255,0.06)',
                              border: '1px solid rgba(255,255,255,0.1)',
                              color: '#fff',
                              fontSize: 11,
                              padding: '3px 8px',
                              borderRadius: 6,
                            }}
                          >
                            ✓ {a.name} {a.quantity > 1 ? `(×${a.quantity})` : ''}
                          </span>
                        ))}
                        {extras.map((ex, i) => (
                          <span
                            key={`ex-${i}`}
                            style={{
                              background: 'rgba(245, 166, 35, 0.15)',
                              border: '1px solid rgba(245, 166, 35, 0.3)',
                              color: '#f5a623',
                              fontSize: 11,
                              padding: '3px 8px',
                              borderRadius: 6,
                            }}
                          >
                            ⭐ Extra: {ex.name} (×{ex.quantity || 1})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Completion Summary if Completed */}
                  {isCompleted && (
                    <div
                      style={{
                        background: 'rgba(39, 200, 90, 0.08)',
                        border: '1px solid rgba(39, 200, 90, 0.25)',
                        borderRadius: 12,
                        padding: '12px 16px',
                        marginTop: 10,
                        fontSize: 12,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ color: '#27C85A', fontWeight: 700 }}>
                          ✓ Completed & Logged: {event.actual_hours_worked || event.scheduled_hours} hrs
                        </span>
                        {event.completed_at && (
                          <span style={{ color: '#7c7c9c' }}>
                            {new Date(event.completed_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      {event.organizer_notes && (
                        <p style={{ margin: '4px 0 0', color: '#a0a0b8', fontStyle: 'italic' }}>
                          "{event.organizer_notes}"
                        </p>
                      )}
                    </div>
                  )}

                  {/* Card Bottom Actions */}
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      marginTop: 18,
                      paddingTop: 16,
                      borderTop: '1px solid rgba(255,255,255,0.06)',
                      flexWrap: 'wrap',
                    }}
                  >
                    {isAccepted && (
                      <button
                        onClick={() => onSelectLiveEvent(event)}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #9F5CFF 0%, #7c3aed 100%)',
                          border: 'none',
                          borderRadius: 10,
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 6,
                        }}
                      >
                        ⚡ Go to Live Event Checklist →
                      </button>
                    )}

                    {isInProgress && (
                      <button
                        onClick={() => onOpenCompleteModal(event)}
                        style={{
                          flex: 1,
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #27C85A 0%, #1fab47 100%)',
                          border: 'none',
                          borderRadius: 10,
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        🏁 Complete Event & Log Hours
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Decline Reason Modal */}
      {declineModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: 10000,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              background: '#121832',
              borderRadius: 16,
              padding: 24,
              border: '1px solid rgba(227,67,100,0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 8px', color: '#E34364', fontSize: 16, fontWeight: 700 }}>
              Decline Event Assignment
            </h3>
            <p style={{ margin: '0 0 16px', color: '#a0a0b8', fontSize: 13 }}>
              Please provide a reason so admin can reassign event #{declineModal.id} ({declineModal.event_date}).
            </p>
            <form onSubmit={handleDeclineSubmit}>
              <textarea
                rows={3}
                required
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g. Prior scheduling conflict, out of town, or transport limitation..."
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  padding: 10,
                  color: '#fff',
                  fontSize: 13,
                  outline: 'none',
                  boxSizing: 'border-box',
                  marginBottom: 16,
                }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setDeclineModal(null)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 8,
                    color: '#a0a0b8',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#E34364',
                    border: 'none',
                    borderRadius: 8,
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {actionLoading ? 'Declining…' : 'Confirm Decline'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
